import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MoreVertical } from "react-feather";
import { useT } from "@reciperoll/core";

export type OverflowItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
};

// A small "⋯" overflow menu — the home for a view's secondary actions. Closes on
// outside click, Escape, or after an item is chosen.
export function OverflowMenu({
  items,
  buttonClassName = "",
  label,
}: {
  items: OverflowItem[];
  buttonClassName?: string;
  label?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // On open, flip the menu above the button when there isn't room below it — so a
  // long menu (or a button low on the page) never runs off the bottom of the view.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimated = Math.min(items.length * 40 + 8, window.innerHeight * 0.7);
    setOpenUp(spaceBelow < estimated && spaceAbove > spaceBelow);
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        aria-label={label ?? t("common.moreActions")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={buttonClassName}
      >
        <MoreVertical className="w-5 h-5" />
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute right-0 z-20 w-52 rounded-xl bg-surface shadow-lg ring-1 ring-line py-1 max-h-[70vh] overflow-y-auto ${
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left hover:bg-surface-2 cursor-pointer ${
                item.destructive ? "text-red-600" : "text-ink"
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
